import React from "react";
import AppLayout from "../components/AppLayout";

export default function About({ entries }) {
  return (
    <AppLayout entries={entries} showRightSidebar={false}>
      <div className="page-prose">
        <h1>About</h1>
        <p>
          {`<${window.SITE_NAME}/>`} is a fast, browser-based workspace for
          formatting and converting code and data. Everything runs locally in
          your browser — nothing is uploaded.
        </p>
        <p>
          Built with Go (Fiber v2) on the server and React + Monaco on the
          client, bundled with esbuild. Formatting is powered by Prettier with
          its full option set exposed in the options panel.
        </p>

        <h2>A Word on Your Data, Plainly Spoken</h2>
        <p>
          Pull up a chair, partner. I&rsquo;ll not waste your daylight. It has
          of late come to light — published plain for any soul who cares to
          read it — that a great many of these online formatting and converting
          outfits do not simply tidy your text and send you on your way. No,
          sir. They keep it. They log it. And more than a few sell it on down
          the line to brokers and middlemen who&rsquo;d sooner trade your
          secrets than shake your hand.
        </p>
        <p>
          Consider, if you will, what an honest body pastes into such a tool:
          configuration files heavy with passwords. API keys that unlock a whole
          company&rsquo;s coffers. Private tokens, customer names, electronic-mail
          addresses, the quiet correspondence of a day&rsquo;s work. You hand it
          over expecting nothing but clean indentation in return — and,
          unbeknownst to you, it rides off to a stranger&rsquo;s server, there to
          be copied, catalogued, and sold like cattle at auction. That is the
          plain shape of the danger, and it is no tall tale.
        </p>

        <h2>On the Matter of Tuna</h2>
        <p>
          There is an old story about the showman P.T. Barnum, who is said to
          have sold canned tuna under a promise stamped right there on the label:{" "}
          <em>this tuna will NOT turn white in the can.</em> Now, the curious
          truth of it is that no tuna turns white in the can — never has, never
          will. Barnum merely said aloud what every can on every shelf could
          have claimed and didn&rsquo;t. Folks heard it, reckoned only his was
          safe, and bought it by the crate.
        </p>
        <p>
          I tell you that tale for a reason, and an honest one. When I say to
          you that {`<${window.SITE_NAME}/>`} will <strong>NOT</strong> sell
          your data, will <strong>NOT</strong> store it, and will{" "}
          <strong>NOT</strong> so much as whisper it to a far-off server — I mean
          every last word. But unlike Barnum&rsquo;s tuna, this promise is no
          parlor trick of phrasing. There the danger was imagined; here it is
          real. The others truly do sell what you give them. We truly do not.
          The same plain words — and a whole world of difference in the truth
          standing behind them.
        </p>

        <blockquote className="pledge">
          We do not store your data. We do not sell your data. We do not upload
          it, log it, or hand it to a third party. Not now, not ever.
        </blockquote>

        <h2>Our Solemn Word</h2>
        <ul>
          <li>
            Every format and conversion is performed right here in your own
            browser, by code already delivered to your machine.
          </li>
          <li>
            Nothing you paste is uploaded, logged, retained, or sold — there is
            no server waiting to receive it.
          </li>
          <li>
            No accounts, and no tracking or analytics of our own. The one
            third party on the premises is the advertising that keeps this tool
            free — spoken of plainly below — and it never gets within arm&rsquo;s
            reach of what you paste.
          </li>
          <li>
            We could not sell your data if we were of a mind to. It never leaves
            your sight to begin with.
          </li>
        </ul>

        <h2>Ads Keep the Lights On</h2>
        <p>
          Now, honesty cuts both ways, so here is the plain truth of how this
          outfit pays its way: we serve advertising through Google AdSense, and
          that — nothing else — is what keeps the doors open and the tool free
          for every soul who wanders in. Like every ad on the web, those load
          Google&rsquo;s own scripts and set their own cookies, and Google may
          note that you stopped by. That is ordinary advertising, the same as any
          newspaper carries — not us peering over your shoulder.
        </p>
        <p>
          But mark this distinction, for it is the whole of the matter: the ads{" "}
          <strong>cannot see what you paste.</strong> Your JSON, your keys, your
          configuration — every byte of it is handled by code running inside your
          own browser and travels nowhere. The advertising sits off to the side
          of the page with no window into your editors. So when we vow that your{" "}
          <em>data</em> never leaves, we mean exactly that — ads or no ads.
        </p>

        <h2>Don&rsquo;t Take My Word — Check the Saddlebags</h2>
        <p>
          A gentleman invites inspection. Open your browser&rsquo;s developer
          tools, mosey on over to the <strong>Network</strong> tab, and paste in
          whatever you please. You will see the advertising going about its
          business — that is the lights staying on — but not one of those
          requests carries the text you pasted. Search high and low: your data
          never rides out to any server of ours, for there is none waiting to
          receive it. The proof sits right out in the open, free for the
          checking. That, I reckon, is the difference between a promise and a
          guarantee.
        </p>

        <h2>Keyboard shortcuts</h2>
        <ul className="shortcut-list">
          <li>
            <span className="kbd">⌘/Ctrl + Enter</span> Convert the input
          </li>
          <li>
            <span className="kbd">⌘/Ctrl + Delete</span> Clear the input,
            output, and struct view
          </li>
          <li>
            <span className="kbd">⌘/Ctrl + Shift + Enter</span> Minify the code
          </li>
          <li>
            <span className="kbd">⌘/Ctrl + I</span> Toggle light / dark theme
          </li>
          <li>
            <span className="kbd">⌘/Ctrl + B</span> Toggle the formatting
            options panel
          </li>
          <li>
            <span className="kbd">⌘/Ctrl + L</span> Toggle the struct viewer
          </li>
        </ul>
      </div>
    </AppLayout>
  );
}
